/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import {useIntl} from 'react-intl'
import {formatPhoneNumber} from '../../utils/phone-utils'
import {useBrand} from '../../contexts/brand-context'

export default function useProfileFields({
    form: {
        control,
        formState: {errors}
    },
    prefix = ''
}) {
    const {formatMessage} = useIntl()
    const {isGarage} = useBrand()

    const fields = {
        firstName: {
            name: `${prefix}firstName`,
            label: formatMessage({
                defaultMessage: 'First Name',
                id: 'use_profile_fields.label.first_name'
            }),
            placeholder: formatMessage({
                defaultMessage: 'First Name',
                id: 'use_profile_fields.label.first_name'
            }),
            type: 'text',
            autoComplete: 'given-name',
            defaultValue: '',
            rules: {
                required: isGarage
                    ? formatMessage({
                          defaultMessage: 'Please enter your first name.',
                          id: 'use_profile_fields.error.required_first_name.garage'
                      })
                    : formatMessage({
                          defaultMessage: 'Please enter your first name.',
                          id: 'use_profile_fields.error.required_first_name.dynamite'
                      })
            },
            error: errors[`${prefix}firstName`],
            control,
            variant: 'webFloatingField',
            inputProps: {
                maxLength: 50
            }
        },
        lastName: {
            name: `${prefix}lastName`,
            label: formatMessage({
                defaultMessage: 'Last Name',
                id: 'use_profile_fields.label.last_name'
            }),
            placeholder: formatMessage({
                defaultMessage: 'Last Name',
                id: 'use_profile_fields.label.last_name'
            }),
            type: 'text',
            defaultValue: '',
            autoComplete: 'family-name',
            rules: {
                required: isGarage
                    ? formatMessage({
                          defaultMessage: 'Please enter your last name.',
                          id: 'use_profile_fields.error.required_last_name.garage'
                      })
                    : formatMessage({
                          defaultMessage: 'Please enter your last name.',
                          id: 'use_profile_fields.error.required_last_name.dynamite'
                      })
            },
            error: errors[`${prefix}lastName`],
            control,
            variant: 'webFloatingField',
            inputProps: {
                maxLength: 50
            }
        },
        email: {
            name: `${prefix}email`,
            label: formatMessage({
                defaultMessage: 'Email Address',
                id: 'use_profile_fields.label.email.address'
            }),
            placeholder: 'you@email.com',
            type: 'email',
            defaultValue: '',
            autoComplete: 'email',
            rules: {
                required: isGarage
                    ? formatMessage({
                          defaultMessage: 'Please enter a valid email address.',
                          id: 'use_profile_fields.error.required_email.garage'
                      })
                    : formatMessage({
                          defaultMessage: 'Please enter a valid email address.',
                          id: 'use_profile_fields.error.required_email.dynamite'
                      })
            },
            error: errors[`${prefix}email`],
            control,
            variant: 'webFloatingField'
        },
        phone: {
            name: `${prefix}phone`,
            label: formatMessage({
                defaultMessage: 'Phone Number',
                id: 'use_profile_fields.label.phone'
            }),
            placeholder: formatMessage({
                defaultMessage: 'Phone Number',
                id: 'use_profile_fields.label.phone'
            }),
            defaultValue: '',
            type: 'tel',
            autoComplete: 'tel',
            rules: {
                validate: (value) => {
                    if (!value) return true

                    const digits = String(value ?? '').replace(/[^\d]/g, '')
                    if (digits.length !== 10) {
                        return formatMessage({
                            defaultMessage: 'Please enter a valid phone number',
                            id: 'use_address_fields.error.invalid_phone_format'
                        })
                    }
                    return true
                }
            },
            error: errors[`${prefix}phone`],
            inputProps: ({onChange}) => ({
                inputMode: 'numeric',
                onChange(evt) {
                    onChange(formatPhoneNumber(evt.target.value))
                }
            }),
            control,
            variant: 'webFloatingField'
        },
        loyaltyId: {
            name: `${prefix}loyaltyId`,
            label: formatMessage({
                defaultMessage: 'Loyalty number ID',
                id: 'use_profile_fields.label.loyalty'
            }),
            placeholder: formatMessage({
                defaultMessage: 'Loyalty number ID',
                id: 'use_profile_fields.label.loyalty'
            }),
            type: 'text',
            defaultValue: '',
            control,
            variant: 'webFloatingField'
        },
        birthDay: {
            name: `${prefix}birthDay`,
            label: formatMessage({
                defaultMessage: 'Birthday',
                id: 'use_profile_fields.label.birthday'
            }),
            placeholder: formatMessage({
                defaultMessage: 'Birthday',
                id: 'use_profile_fields.label.birthday'
            }),
            defaultValue: '',
            autoComplete: 'birthDay',
            rules: {
                validate: (value) => {
                    if (!value) return true

                    const birthDate = new Date(value)
                    const today = new Date()

                    const age = Math.floor((today - birthDate) / (365.25 * 24 * 60 * 60 * 1000))

                    if (age < 16) {
                        return isGarage
                            ? formatMessage({
                                  defaultMessage: 'You must be at least 16 years old',
                                  id: 'use_registration_fields.error.min_age.garage'
                              })
                            : formatMessage({
                                  defaultMessage: 'You must be at least 16 years old',
                                  id: 'use_registration_fields.error.min_age.dynamite'
                              })
                    }

                    return true
                }
            },
            error: errors[`${prefix}birthDay`],
            control,
            variant: 'webFloatingField'
        }
    }

    return fields
}
